/**
 * Structured Error Logger for "What To Eat?"
 * Provides centralized error handling with severity levels.
 * Logs to: console (dev), Firestore error-logs collection (prod)
 * 
 * Severity Levels:
 *   INFO     - Informational messages, non-error events
 *   WARNING  - Potential issues that don't break functionality
 *   ERROR    - Recoverable errors that affect a single operation
 *   CRITICAL - System-level failures that require immediate attention
 * 
 * Usage:
 *   ErrorLogger.info('ModuleName', 'Operation completed', { details });
 *   ErrorLogger.warning('ModuleName', 'Potential issue', { details });
 *   ErrorLogger.error('ModuleName', 'Operation failed', error);
 *   ErrorLogger.critical('ModuleName', 'System failure', error);
 */

(function() {
  'use strict';

  const SEVERITY = {
    INFO: 'INFO',
    WARNING: 'WARNING',
    ERROR: 'ERROR',
    CRITICAL: 'CRITICAL'
  };

  const MAX_BATCH_SIZE = 10;
  let logBatch = [];
  let batchTimer = null;

  class StructuredErrorLogger {
    constructor() {
      this.initialized = false;
      this.db = null;
      this.sessionId = this._generateSessionId();
      this.logCount = 0;
      this.errorCount = 0;
    }

    /**
     * Initialize the logger. Must be called after Firebase is ready.
     * @returns {Promise<boolean>} True if initialized successfully
     */
    async init() {
      if (this.initialized) return true;

      try {
        // Try to get Firestore instance from window globals
        if (window.firebaseDbInstance) {
          this.db = window.firebaseDbInstance;
          this.initialized = true;
          console.log('[ErrorLogger] Initialized with Firestore logging');
          return true;
        }

        // Wait for Firebase to be ready
        if (typeof window.ensureFirebaseReady === 'function') {
          const result = await window.ensureFirebaseReady();
          if (result && result.db) {
            this.db = result.db;
            this.initialized = true;
            console.log('[ErrorLogger] Initialized with Firestore logging');
            return true;
          }
        }

        console.log('[ErrorLogger] Firestore not available - console-only logging');
        this.initialized = true; // Still mark as initialized for console-only mode
        return true;
      } catch (e) {
        console.warn('[ErrorLogger] Init failed:', e.message);
        this.initialized = true; // Allow console-only logging
        return true;
      }
    }

    /**
     * Generate a unique session ID for tracking errors across page loads
     */
    _generateSessionId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    }

    /**
     * Get the current user info for context
     */
    _getUserContext() {
      try {
        const auth = window.firebaseAuthInstance;
        if (auth && auth.currentUser) {
          return {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email
          };
        }
      } catch (e) {
        // Ignore auth errors
      }
      return null;
    }

    /**
     * Create a structured log entry
     */
    _createLogEntry(severity, source, message, details, error) {
      const entry = {
        severity: severity,
        source: source,
        message: message,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        user: this._getUserContext(),
        details: details || null,
        error: null,
        logNumber: ++this.logCount
      };

      if (error) {
        entry.error = {
          message: error.message || String(error),
          code: error.code || null,
          stack: error.stack || null,
          name: error.name || null
        };
      }

      return entry;
    }

    /**
     * Log to console with appropriate formatting
     */
    _consoleLog(entry) {
      const prefix = `[${entry.severity}][${entry.source}]`;
      const timestamp = entry.timestamp.split('.')[0].replace('T', ' ');

      switch (entry.severity) {
        case SEVERITY.CRITICAL:
          console.error(`%c${prefix} ${entry.message}`, 'color: #e74c3c; font-weight: bold; font-size: 14px;', {
            timestamp,
            details: entry.details,
            error: entry.error,
            user: entry.user,
            url: entry.url
          });
          break;
        case SEVERITY.ERROR:
          console.error(`%c${prefix} ${entry.message}`, 'color: #e74c3c;', {
            timestamp,
            details: entry.details,
            error: entry.error
          });
          break;
        case SEVERITY.WARNING:
          console.warn(`%c${prefix} ${entry.message}`, 'color: #f39c12;', {
            timestamp,
            details: entry.details
          });
          break;
        case SEVERITY.INFO:
          console.log(`%c${prefix} ${entry.message}`, 'color: #3498db;', {
            timestamp,
            details: entry.details
          });
          break;
      }
    }

    /**
     * Check if the user is currently authenticated with Firebase Auth.
     * @returns {boolean} True if an authenticated user exists
     */
    _isAuthenticated() {
      try {
        // Check if Firebase Auth instance exists and has a current user
        if (window.firebaseAuthInstance && window.firebaseAuthInstance.currentUser) {
          return true;
        }
        // Also check the modular SDK if available
        if (window.firebaseAuthInstance && typeof window.firebaseAuthInstance.currentUser !== 'undefined') {
          return window.firebaseAuthInstance.currentUser !== null;
        }
        return false;
      } catch (e) {
        return false;
      }
    }

    /**
     * Write log entry to Firestore error-logs collection
     * CRITICAL FIX: Only write to Firestore if the user is authenticated.
     * Firestore security rules require isAuthenticated() for error-logs collection.
     * When logged out, logs are console-only to prevent permission errors.
     */
    async _firestoreLog(entry) {
      if (!this.initialized || !this.db || !this.db.collection) {
        return;
      }

      // ============ FIX: Check authentication before Firestore write ============
      // If the user is not authenticated, skip Firestore write entirely.
      // This prevents the "Missing or insufficient permissions" error that occurs
      // when the anonymous (unauthenticated) user tries to write to error-logs.
      if (!this._isAuthenticated()) {
        return; // Console-only mode when logged out
      }
      // ============ END FIX ============

      try {
        // Add to batch
        logBatch.push(entry);

        // Flush batch if threshold reached
        if (logBatch.length >= MAX_BATCH_SIZE) {
          await this._flushBatch();
        } else if (!batchTimer) {
          // Schedule batch flush after 5 seconds
          batchTimer = setTimeout(() => this._flushBatch(), 5000);
        }
      } catch (e) {
        // Don't log errors about logging - just console it
        console.warn('[ErrorLogger] Firestore write failed:', e.message);
      }
    }

    /**
     * Flush buffered log entries to Firestore
     */
    async _flushBatch() {
      if (batchTimer) {
        clearTimeout(batchTimer);
        batchTimer = null;
      }

      if (logBatch.length === 0) return;

      const batch = logBatch.splice(0, MAX_BATCH_SIZE);
      
      try {
        // Use batched writes for efficiency
        if (this.db.batch) {
          const firestoreBatch = this.db.batch();
          batch.forEach(entry => {
            const docRef = this.db.collection('error-logs').doc();
            firestoreBatch.set(docRef, entry);
          });
          await firestoreBatch.commit();
        } else {
          // Fallback to individual writes
          const promises = batch.map(entry => 
            this.db.collection('error-logs').add(entry)
          );
          await Promise.allSettled(promises);
        }
      } catch (e) {
        console.warn('[ErrorLogger] Batch flush failed:', e.message);
        // Re-queue failed entries (but avoid infinite loops)
        if (batch.length > 0 && this.errorCount < 3) {
          this.errorCount++;
          setTimeout(() => {
            logBatch.push(...batch);
            this._flushBatch();
          }, 1000);
        }
      }
    }

    // ============ PUBLIC API ============

    /**
     * Log an informational message
     * @param {string} source - Module or function name
     * @param {string} message - Description of the event
     * @param {object} [details] - Additional contextual data
     */
    info(source, message, details) {
      const entry = this._createLogEntry(SEVERITY.INFO, source, message, details);
      this._consoleLog(entry);
      this._firestoreLog(entry);
    }

    /**
     * Log a warning message
     * @param {string} source - Module or function name
     * @param {string} message - Description of the potential issue
     * @param {object} [details] - Additional contextual data
     */
    warning(source, message, details) {
      const entry = this._createLogEntry(SEVERITY.WARNING, source, message, details);
      this._consoleLog(entry);
      this._firestoreLog(entry);
    }

    /**
     * Forward an entry to Sentry if the SDK is loaded.
     * Only fires for ERROR and CRITICAL severity to stay within quota.
     * @private
     */
    _sentryCapture(entry, originalError) {
      try {
        const sentry = window.__sentryCapture;
        if (!sentry) return;

        const context = {
          source: entry.source,
          sessionId: entry.sessionId,
          url: entry.url,
          ...(entry.details || {}),
        };

        if (originalError instanceof Error) {
          sentry.exception(originalError, context);
        } else {
          sentry.message(
            `[${entry.source}] ${entry.message}`,
            entry.severity === SEVERITY.CRITICAL ? 'fatal' : 'error',
            context,
          );
        }

        // Keep Sentry user context in sync with Firebase Auth
        const user = entry.user;
        if (user && user.uid) {
          sentry.setUser({ id: user.uid, email: user.email || undefined });
        }
      } catch (e) {
        // Never let Sentry forwarding break the app
      }
    }

    /**
     * Log an error message
     * @param {string} source - Module or function name
     * @param {string} message - Description of the error
     * @param {Error|object} [error] - Error object with details
     * @param {object} [details] - Additional contextual data
     */
    error(source, message, error, details) {
      const entry = this._createLogEntry(SEVERITY.ERROR, source, message, details, error);
      this._consoleLog(entry);
      this._firestoreLog(entry);
      this._sentryCapture(entry, error);
    }

    /**
     * Log a critical error
     * @param {string} source - Module or function name
     * @param {string} message - Description of the critical failure
     * @param {Error|object} [error] - Error object with details
     * @param {object} [details] - Additional contextual data
     */
    critical(source, message, error, details) {
      const entry = this._createLogEntry(SEVERITY.CRITICAL, source, message, details, error);
      this._consoleLog(entry);
      this._firestoreLog(entry);
      this._sentryCapture(entry, error);

      // For critical errors, also show user notification
      if (typeof showNotification === 'function') {
        showNotification('A critical error occurred. Please check the console for details.', 'error');
      }
    }

    /**
     * Log a Firebase operation result with timing
     * @param {string} source - Module or function name
     * @param {string} operation - The Firebase operation (e.g., 'setDoc', 'getDocs')
     * @param {string} collection - The Firestore collection
     * @param {number} durationMs - Operation duration in milliseconds
     * @param {boolean} success - Whether the operation succeeded
     * @param {object} [details] - Additional contextual data
     */
    logFirebaseOperation(source, operation, collection, durationMs, success, details) {
      const level = success ? SEVERITY.INFO : SEVERITY.ERROR;
      const status = success ? 'SUCCESS' : 'FAILED';
      const message = `Firestore ${operation} on ${collection} ${status} (${durationMs}ms)`;
      
      const entry = this._createLogEntry(level, source, message, {
        firestoreOperation: operation,
        firestoreCollection: collection,
        durationMs: durationMs,
        success: success,
        ...details
      });
      
      this._consoleLog(entry);
      
      // Only log failed operations to Firestore to reduce noise
      if (!success) {
        this._firestoreLog(entry);
      }
    }

    /**
     * Get error statistics for the current session
     */
    getStats() {
      return {
        sessionId: this.sessionId,
        logCount: this.logCount,
        initialized: this.initialized
      };
    }

    /**
     * Flush any pending log entries
     */
    async flush() {
      await this._flushBatch();
    }
  }

  // Create singleton instance
  const ErrorLogger = new StructuredErrorLogger();

  // Expose globally
  window.ErrorLogger = ErrorLogger;

  // Auto-initialize when Firebase is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Initialization will happen on first log call if needed
      ErrorLogger.init();
    });
  } else {
    ErrorLogger.init();
  }

  console.log('[ErrorLogger] Structured error logger loaded');

})();
